import { useEffect, useMemo, useState } from "react";
import type { Hasher, Kennel } from "@hash/shared";
import { api } from "../api/client";
import {
  addHasher,
  loadHashers,
  reloadHashers,
  useHashers,
} from "../stores/hasherStore";
import { parseApiError } from "../api/errors";
import { AccountStateBadge, MismanBadge } from "../components/HasherBadges";
import { formatCalendarDate } from "../lib/trailDate";

type SortMode = "name" | "kennel" | "lastHashed";

// Last-hashed filter options. Values are weeks of lookback; 0 means "any".
const SINCE_OPTIONS: Array<{ label: string; weeks: number }> = [
  { label: "Any time", weeks: 0 },
  { label: "Last 4 weeks", weeks: 4 },
  { label: "Last 3 months", weeks: 13 },
  { label: "Last 6 months", weeks: 26 },
  { label: "Last year", weeks: 52 },
];

export function HashersPage({
  onOpenHasher,
}: {
  // Open a hasher's Manage screen. Optional so the page still renders
  // standalone (e.g. in tests) without navigation wired.
  onOpenHasher?: (hasherId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortMode>("name");
  const [kennelId, setKennelId] = useState<string>("");
  const [sinceWeeks, setSinceWeeks] = useState<number>(0);

  const [kennels, setKennels] = useState<Kennel[]>([]);
  // Ids of hashers who hold a misman role on some kennel — drives the roster
  // badge. Aggregated from the managed kennels' misman rosters (misman only
  // exist on managed kennels).
  const [mismanIds, setMismanIds] = useState<Set<string>>(new Set());
  // The roster lives in the shared client-side store (loaded once on app open,
  // searched locally). We refresh it on mount in case it's gone stale.
  const { hashers, status, error: loadError } = useHashers();
  // Whether the create-hasher modal is open (Story 11.1).
  const [creating, setCreating] = useState(false);

  // Load all kennels (for the filter dropdown) and the misman rosters of the
  // managed ones (for the roster badge). The lists are small (one row per
  // chapter) so we don't bother paginating the rosters.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const collected: Kennel[] = [];
      let next: string | undefined = undefined;
      do {
        const res = await api.kennels.list({ cursor: next, limit: 200 });
        if (cancelled) return;
        collected.push(...res.kennels);
        next = res.cursor;
      } while (next);
      if (cancelled) return;
      setKennels(collected);

      const rosters = await Promise.all(
        collected
          .filter((k) => k.managed)
          .map((k) =>
            api.kennels
              .listMisman(k.id)
              .then((r) => r.misman)
              .catch(() => [])
          )
      );
      if (cancelled) return;
      setMismanIds(new Set(rosters.flat().map((a) => a.hasherId)));
    })().catch((e) => console.error("Failed to load kennels/misman", e));
    return () => {
      cancelled = true;
    };
  }, []);

  // The roster is short and rarely changes, so refreshing on mount picks up a
  // stale store (>10 min old) without a per-keystroke server round-trip.
  useEffect(() => {
    loadHashers().catch((e) => console.error("Failed to load hashers", e));
  }, []);

  // `since` cutoff as a YYYY-MM-DD date (lastAttendedAt is date-granular).
  const sinceDate = useMemo(() => {
    if (!sinceWeeks) return undefined;
    const d = new Date();
    d.setDate(d.getDate() - sinceWeeks * 7);
    return d.toISOString().slice(0, 10);
  }, [sinceWeeks]);

  // All filtering happens client-side over the store snapshot: substring name
  // match, home-kennel, and last-hashed cutoff (mirrors the old server filters).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return hashers.filter((h) => {
      if (q && !h.name.toLowerCase().includes(q)) return false;
      if (kennelId && h.homeKennelId !== kennelId) return false;
      if (sinceDate) {
        if (!h.lastAttendedAt || h.lastAttendedAt.slice(0, 10) < sinceDate) {
          return false;
        }
      }
      return true;
    });
  }, [hashers, query, kennelId, sinceDate]);

  // Sort is applied client-side. "name" matches the server's natural GSI1
  // order; "kennel" and "lastHashed" reorder locally.
  const sorted = useMemo(() => {
    const list = [...filtered];
    switch (sort) {
      case "name":
        list.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "kennel":
        list.sort((a, b) => {
          const k = a.homeKennel.localeCompare(b.homeKennel);
          return k !== 0 ? k : a.name.localeCompare(b.name);
        });
        break;
      case "lastHashed":
        list.sort((a, b) => {
          // Most recent first; never-attended sort to the end.
          if (!a.lastAttendedAt && !b.lastAttendedAt) {
            return a.name.localeCompare(b.name);
          }
          if (!a.lastAttendedAt) return 1;
          if (!b.lastAttendedAt) return -1;
          return b.lastAttendedAt.localeCompare(a.lastAttendedAt);
        });
        break;
    }
    return list;
  }, [filtered, sort]);

  return (
    <div className="min-h-screen pb-24">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-bold text-slate-900">Hashers</h2>
          <button
            onClick={() => setCreating(true)}
            className="shrink-0 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white active:bg-slate-700"
          >
            + New hasher
          </button>
        </div>
        <input
          type="search"
          inputMode="search"
          placeholder="Search hashers…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => loadHashers().catch(() => {})}
          className="w-full h-12 px-4 rounded-lg bg-slate-100 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <LabeledSelect
            label="Sort"
            value={sort}
            onChange={(v) => setSort(v as SortMode)}
            options={[
              { value: "name", label: "Hash name" },
              { value: "kennel", label: "Kennel, then hash name" },
              { value: "lastHashed", label: "Last hashed" },
            ]}
          />
          <LabeledSelect
            label="Kennel"
            value={kennelId}
            onChange={setKennelId}
            options={[
              { value: "", label: "All kennels" },
              ...kennels.map((k) => ({ value: k.id, label: k.name })),
            ]}
          />
          <LabeledSelect
            label="Last hashed"
            value={String(sinceWeeks)}
            onChange={(v) => setSinceWeeks(Number(v))}
            options={SINCE_OPTIONS.map((o) => ({
              value: String(o.weeks),
              label: o.label,
            }))}
          />
        </div>
      </header>

      {status === "error" && loadError && (
        <p className="px-4 py-3 text-sm text-red-600">{loadError}</p>
      )}

      {sorted.length === 0 && status === "loading" ? (
        <p className="px-4 py-6 text-sm text-slate-500">Loading…</p>
      ) : sorted.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-500">No hashers found.</p>
      ) : (
        <ul className="divide-y divide-slate-200 bg-white">
          {sorted.map((h) => (
            <li key={h.id}>
              <button
                type="button"
                onClick={() => onOpenHasher?.(h.id)}
                disabled={!onOpenHasher}
                aria-label={`Manage ${h.name}`}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 disabled:cursor-default disabled:hover:bg-transparent"
              >
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate font-medium text-slate-900">
                      {h.name}
                    </span>
                    <AccountStateBadge state={h.accountState} />
                    {mismanIds.has(h.id) && <MismanBadge />}
                  </span>
                  <span className="block text-sm text-slate-500">
                    {h.homeKennel}
                    {h.phone && <> · {h.phone}</>}
                    {h.lastAttendedAt && (
                      <> · last hashed {formatDate(h.lastAttendedAt)}</>
                    )}
                  </span>
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className="shrink-0 text-slate-300"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      {creating && (
        <CreateHasherModal
          kennels={kennels}
          onClose={() => setCreating(false)}
          onCreated={(created, newKennel) => {
            if (newKennel) setKennels((prev) => [...prev, newKennel]);
            addHasher(created);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Add a hasher outside of check-in (Story 11.1). Creates a lite, no-phone
 * record — phone is attached afterward via the phone button, which routes
 * through the Cognito-aware attach flow (the create endpoint doesn't). Home
 * kennel offers inline reference-kennel creation (Story 3.1); the visitor
 * default derives from the selected kennel's managed-ness, overridable.
 */
function CreateHasherModal({
  kennels,
  onClose,
  onCreated,
}: {
  kennels: Kennel[];
  onClose: () => void;
  onCreated: (hasher: Hasher, newKennel?: Kennel) => void;
}) {
  const NEW_KENNEL = "__new__";
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [nerdName, setNerdName] = useState("");
  const [email, setEmail] = useState("");
  const [kennelChoice, setKennelChoice] = useState<string>(
    kennels[0]?.id ?? NEW_KENNEL
  );
  const [newKennelName, setNewKennelName] = useState("");
  // null = follow the kennel-derived default; otherwise an explicit override.
  const [visitorOverride, setVisitorOverride] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isNewKennel = kennelChoice === NEW_KENNEL;
  const selectedKennel = kennels.find((k) => k.id === kennelChoice);
  // Reference kennel → visitor; managed → local. A new kennel is a reference
  // kennel, so it defaults to visitor.
  const visitorDefault = isNewKennel ? true : selectedKennel?.managed !== true;
  const visitor = visitorOverride ?? visitorDefault;

  const trimmedName = name.trim();
  const trimmedNewKennel = newKennelName.trim();
  const canSubmit =
    !busy &&
    trimmedName.length > 0 &&
    (isNewKennel ? trimmedNewKennel.length > 0 : kennelChoice.length > 0);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      let homeKennelId = kennelChoice;
      let createdKennel: Kennel | undefined;
      if (isNewKennel) {
        const match = kennels.find(
          (k) => k.name.toLowerCase() === trimmedNewKennel.toLowerCase()
        );
        if (match) {
          homeKennelId = match.id;
        } else {
          createdKennel = await api.kennels.create({ name: trimmedNewKennel });
          homeKennelId = createdKennel.id;
        }
      }
      const hasher = await api.hashers.create({
        name: trimmedName,
        displayName: displayName.trim() || undefined,
        nerdName: nerdName.trim() || undefined,
        email: email.trim() || undefined,
        homeKennelId,
        visitor,
      });
      onCreated(hasher, createdKennel);
    } catch (e) {
      // A 409 means the name already exists (someone created it since our store
      // last loaded) — refresh the store so the existing hasher shows up.
      if (parseApiError(e).status === 409) void reloadHashers();
      setErr(extractError(e, "Failed to create hasher"));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center bg-slate-900/40 sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Create hasher"
        className="w-full rounded-t-2xl bg-white p-5 sm:max-w-sm sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-slate-900">New hasher</h2>
        <p className="mt-1 text-sm text-slate-500">
          Creates a lite record. Add a phone afterward so they can log in.
        </p>
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Hash name
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="Hash name"
              autoFocus
              className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Display name <span className="font-normal normal-case">(optional)</span>
            </span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              aria-label="Display name"
              placeholder="Shown on the public hareline instead of the hash name"
              className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Nerd name <span className="font-normal normal-case">(optional)</span>
            </span>
            <input
              value={nerdName}
              onChange={(e) => setNerdName(e.target.value)}
              aria-label="Nerd name"
              className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Email <span className="font-normal normal-case">(optional)</span>
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label="Email"
              className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Home kennel
            </span>
            <select
              value={kennelChoice}
              onChange={(e) => {
                setKennelChoice(e.target.value);
                setVisitorOverride(null);
              }}
              aria-label="Home kennel"
              className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              {[...kennels]
                .sort((a, b) => {
                  // Managed kennels first
                  if (a.managed !== b.managed) return b.managed ? 1 : -1;
                  // Then alphabetical
                  return a.name.localeCompare(b.name);
                })
                .map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name}
                  </option>
                ))}
              <option value={NEW_KENNEL}>+ New kennel…</option>
            </select>
            {isNewKennel && (
              <input
                value={newKennelName}
                onChange={(e) => setNewKennelName(e.target.value)}
                placeholder="New kennel name"
                aria-label="New kennel name"
                className="mt-2 h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            )}
          </label>
          <button
            type="button"
            role="switch"
            aria-checked={visitor}
            aria-label="Visitor"
            onClick={() => setVisitorOverride(!visitor)}
            className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-3"
          >
            <span className="font-medium text-slate-900">Visitor</span>
            <span
              className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                visitor ? "bg-emerald-600" : "bg-slate-300"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  visitor ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </span>
          </button>
        </div>
        {err && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {err}
          </p>
        )}
        <div className="mt-4 flex gap-2">
          <button
            onClick={onClose}
            className="h-12 flex-1 rounded-lg bg-slate-100 font-medium text-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="h-12 flex-1 rounded-lg bg-slate-900 font-medium text-white disabled:opacity-50"
          >
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LabeledSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="flex flex-col text-xs font-medium text-slate-500">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function extractError(err: unknown, fallback: string): string {
  if (!(err instanceof Error)) return fallback;
  // The api client throws `Error("<status>: <body>")`; the body is usually
  // JSON like `{"error":"..."}`. Pull that out for a clean message.
  const match = err.message.match(/^\d+:\s*(.*)$/);
  const body = match ? match[1] : err.message;
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed?.error === "string") return parsed.error;
  } catch {
    // fall through
  }
  return body || fallback;
}

// lastAttendedAt is a trail calendar date (YYYY-MM-DD). Format it zone-safely so
// the displayed day matches the trail's date for every viewer (legacy datetime
// values are accepted and read at date granularity).
function formatDate(value: string): string {
  return formatCalendarDate(value, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
